import { getDocuments } from '../services/firebase';
import { Outlet } from '../types';

export interface OutletFilterResult {
  outlets: Outlet[];
  userRoute: any;
  error?: string;
}

/**
 * Reusable outlet filtering function for both Dashboard and Sales
 * @param currentUserId - The current user's ID
 * @param statusFilter - 'all' to show all outlets, 'active' to show only active outlets
 * @returns Filtered outlets with route information
 */
export const getFilteredOutletsForUser = async (
  currentUserId: string,
  statusFilter: 'all' | 'active' = 'all'
): Promise<OutletFilterResult> => {
  try {
    const today = new Date().toISOString().split('T')[0];

    // Get all required data
    const [routesData, outletsData, beats, localities] = await Promise.all([
      getDocuments('routes'),
      getDocuments('outlets'),
      getDocuments('beats'),
      getDocuments('localities')
    ]);

    // Find user's active beat assignment for today
    const userActiveBeat = beats.find((beat: any) => 
      beat.salesmanId === currentUserId && 
      beat.isActive && 
      beat.forDate === today
    );

    if (!userActiveBeat || !userActiveBeat.routeId) {
      return {
        outlets: [],
        userRoute: null,
        error: 'No route assigned for today. Please contact administrator.'
      };
    }

    // Get route information
    const userRoute = routesData.find((route: any) => route.routeId === userActiveBeat.routeId);
    if (!userRoute) {
      return {
        outlets: [],
        userRoute: null,
        error: 'Route configuration not found. Please contact administrator.'
      };
    }
    if (!userRoute.routeId) {
      return {
        outlets: [],
        userRoute: null,
        error: 'Assigned route is missing required routeId. Please contact administrator.'
      };
    }

    // Get localities assigned to this route
    const routeLocalities = localities.filter((locality: any) => locality.routeId === userActiveBeat.routeId);
    // Hard customId policy: join by business IDs only
    const routeLocalityIds = routeLocalities.map((locality: any) => locality.id);

    // Filter outlets by route localities
    let filteredOutlets = outletsData.filter((outlet: Outlet) => 
      outlet.localityId && routeLocalityIds.includes(outlet.localityId)
    );

    // Apply status filter
    if (statusFilter === 'active') {
      filteredOutlets = filteredOutlets.filter((outlet: Outlet) => 
        outlet.status === 'active'
      );
    }

    // Sort outlets by sequence number
    const sortedOutlets = filteredOutlets.sort((a, b) => {
      if (a.outletSequence && b.outletSequence) {
        return a.outletSequence - b.outletSequence;
      }
      if (a.outletSequence && !b.outletSequence) return -1;
      if (!a.outletSequence && b.outletSequence) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });

    return {
      outlets: sortedOutlets,
      userRoute,
      error: undefined
    };

  } catch (error) {
    console.error('Error filtering outlets:', error);
    return {
      outlets: [],
      userRoute: null,
      error: 'Error loading outlet data. Please try again.'
    };
  }
};